import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { StarPicker } from "@/components/review/StarPicker";
import { ReviewPhotoPicker } from "@/components/review/ReviewPhotoPicker";

export const QUOTE_MAX = 2000;

const errorText = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  return err?.response ? "Could not send your review — please try again." : "Can't reach the server — check your connection and try again.";
};

export function ReviewForm({ initialName = "", onDone }) {
  const [form, setForm] = useState({ name: initialName, where: "", quote: "", stars: 5, email: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const tooLong = form.quote.length > QUOTE_MAX;

  const submit = async (e) => {
    e.preventDefault();
    if (tooLong) { toast.error(`A little long — keep it under ${QUOTE_MAX} characters.`); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    photos.forEach((p) => fd.append("photos", p));
    try { await api.post("/reviews/submit", fd); onDone(form); }
    catch (err) { toast.error(errorText(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="dg-card rounded-2xl p-5 sm:p-6 grid gap-5" data-testid="public-review-form">
      <div>
        <div className="dg-eyebrow mb-2">How did David do?</div>
        <StarPicker value={form.stars} onChange={(n) => setForm((f) => ({ ...f, stars: n }))} />
      </div>
      <label className="block">
        <div className="dg-eyebrow mb-1 flex items-center justify-between"><span>Your review</span>
          <span className={`font-mono normal-case tracking-normal ${tooLong ? "text-red-400" : "text-slate-500"}`} data-testid="public-review-count">{form.quote.length} / {QUOTE_MAX}</span></div>
        <textarea className={`dg-input min-h-[140px] ${tooLong ? "border-red-500/60" : ""}`} value={form.quote} onChange={set("quote")} required
          placeholder="What was the problem, and how did it go? A sentence or two is perfect." data-testid="public-review-quote" />
      </label>
      <ReviewPhotoPicker photos={photos} setPhotos={setPhotos} prefix="public-review" />
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block"><div className="dg-eyebrow mb-1">Your name</div>
          <input className="dg-input" value={form.name} onChange={set("name")} maxLength={80} required placeholder="e.g. Marina S." data-testid="public-review-name" /></label>
        <label className="block"><div className="dg-eyebrow mb-1">City or what we fixed (optional)</div>
          <input className="dg-input" value={form.where} onChange={set("where")} maxLength={80} placeholder="e.g. Brentwood · TV mount" data-testid="public-review-where" /></label>
      </div>
      <label className="block"><div className="dg-eyebrow mb-1">Email (optional — never shown, just so David can say thanks)</div>
        <input type="email" className="dg-input" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="public-review-email" /></label>
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-xs text-slate-500">David reads every review{photos.length ? " and photo" : ""} before it goes on the site — usually within a day.</div>
        <button type="submit" disabled={busy} className="dg-btn-primary rounded-full px-6 py-3 min-h-[44px] text-sm flex items-center justify-center gap-2 disabled:opacity-50 w-full sm:w-auto" data-testid="public-review-submit">
          <Send className="w-4 h-4" /> {busy ? (photos.length ? "Sending photos…" : "Sending…") : "Send my review"}
        </button>
      </div>
    </form>
  );
}
